import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Cart.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faTrash,
  faMinus,
  faPlus,
  faShoppingCart,
  faArrowRight,
  faTag,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../Context/Cart';
import { useLanguage } from '../../Context/Language';
import { getCartImage, getImageUrl } from '../../Utils/imageUtils';
import { formatPrice } from '../../Utils/formatUtils';

const Cart = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    cart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartCount,
    cartSubtotal,
    cartTotal,
    shippingCost,
    discount,
    discountAmount,
    discountLoading,
    discountError,
    applyDiscount,
    removeDiscount,
    isCartOpen,
    closeCart,
  } = useCart();

  const hasSaleItems = cart.some(item => item.on_sale === 1);
  const canApplyDiscount = !hasSaleItems;

  const [discountCode, setDiscountCode] = useState('');

  const handleApplyDiscount = async (e) => {
    e.preventDefault();
    const success = await applyDiscount(discountCode);
    if (success) {
      setDiscountCode('');
    }
  };

  return (
    <>
      <div
        className={`drawer-overlay ${isCartOpen ? 'active' : ''}`}
        onClick={closeCart}
      />
      <div className={`drawer ${isCartOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h3 className="drawer-title">
            <FontAwesomeIcon icon={faShoppingCart} />
            {t('Cart')} ({cartCount})
          </h3>
          <button type="button" className="drawer-close" onClick={closeCart} aria-label={t('Close cart')}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="drawer-empty">
            <FontAwesomeIcon icon={faShoppingCart} className="drawer-empty-icon" />
            <p>{t('Your cart is empty')}</p>
            <button type="button" className="drawer-continue" onClick={closeCart}>
              {t('Continue Shopping')}
            </button>
          </div>
        ) : (
          <>
            <div className="drawer-items">
              {cart.map((item) => {
                const originalImgSrc = getCartImage(item);
                const imgSrc = getImageUrl(originalImgSrc);
                return (
                <div key={item.lineKey} className="drawer-item">
                  <Link
                    to={`/products/${item.slug}`}
                    className="drawer-item-image"
                    onClick={closeCart}
                  >
                    <img
                      src={imgSrc}
                      alt={item.name}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        if (!e.target.dataset.directFallback) {
                          e.target.dataset.directFallback = 'true';
                          e.target.src = originalImgSrc;
                          return;
                        }
                        e.target.onerror = null;
                        e.target.src = 'https://placehold.co/400x300';
                      }}
                    />
                  </Link>

                  <div className="drawer-item-info">
                    <Link
                      to={`/products/${item.slug}`}
                      className="drawer-item-name"
                      onClick={closeCart}
                    >
                      {item.name}
                    </Link>

                    {item.variantName && (
                      <span className="drawer-item-variant">{item.variantName}</span>
                    )}

                    <div className="drawer-item-price-wrap">
                      <span className="drawer-item-price">
                        {formatPrice(item.price)}
                      </span>
                      {item.compare_price && item.price < item.compare_price && (
                        <span className="drawer-item-sale">
                          {t('Sale')}
                        </span>
                      )}
                    </div>

                    <div className="drawer-item-bottom">
                      <div className="drawer-item-qty">
                        <button
                          type="button"
                          className="dqty-btn"
                          onClick={() => updateQuantity(item.lineKey, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          aria-label={t('Decrease quantity')}
                        >
                          <FontAwesomeIcon icon={faMinus} />
                        </button>
                        <span className="dqty-value">{item.quantity}</span>
                        <button
                          type="button"
                          className="dqty-btn"
                          onClick={() => updateQuantity(item.lineKey, item.quantity + 1)}
                          disabled={item.quantity >= (item.stock || 999)}
                          aria-label={t('Increase quantity')}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      </div>

                      <span className="drawer-item-total">
                        {formatPrice(item.price * item.quantity)}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="drawer-item-remove"
                    onClick={() => removeFromCart(item.lineKey)}
                    aria-label={t('Remove item')}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              )})}
            </div>

            <div className="drawer-footer">
              <button type="button" className="drawer-clear" onClick={clearCart}>
                {t('Clear Cart')}
              </button>

              <div className="drawer-discount">
                {hasSaleItems && !discount && (
                  <div className="discount-warning">
                    <FontAwesomeIcon icon={faTag} className="warning-icon" />
                    <span className="warning-text">
                      {t('Sale items cannot use discount codes')}
                    </span>
                  </div>
                )}
                {discount ? (
                  <div className="discount-applied">
                    <div className="discount-info">
                      <FontAwesomeIcon icon={faTag} />
                      <span className="discount-code">{discount.code}</span>
                      <span className="discount-value">
                        {discount.type === 'percentage'
                          ? `-${discount.value}%`
                          : `-${formatPrice(discount.value)}`}
                      </span>
                    </div>
                    <button type="button" className="discount-remove" onClick={removeDiscount} aria-label={t('Remove discount')}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ) : (
                  <form className="discount-form" onSubmit={handleApplyDiscount} style={{ opacity: canApplyDiscount ? 1 : 0.6 }}>
                    <div className="discount-input-wrap">
                      <FontAwesomeIcon icon={faTag} className="discount-icon" />
                      <input
                        type="text"
                        className="discount-input"
                        placeholder={t('Discount code')}
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        disabled={discountLoading || !canApplyDiscount}
                      />
                    </div>
                    <button
                      type="submit"
                      className="discount-btn"
                      disabled={discountLoading || !discountCode.trim() || !canApplyDiscount}
                    >
                      {discountLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        t('Apply')
                      )}
                    </button>
                  </form>
                )}
                {discountError && (
                  <span className="discount-error">{discountError}</span>
                )}
              </div>

              <div className="drawer-summary">
                <div className="drawer-row">
                  <span>{t('Subtotal')}</span>
                  <span>{formatPrice(cartSubtotal)}</span>
                </div>

                {discountAmount > 0 && (
                  <div className="drawer-row drawer-row-discount">
                    <span>{t('Discount')}</span>
                    <span>-{formatPrice(discountAmount)}</span>
                  </div>
                )}

                <div className="drawer-row">
                  <span>{t('Shipping')}</span>
                  <span>{shippingCost === 0 ? t('Free') : formatPrice(shippingCost)}</span>
                </div>

                <div className="drawer-divider" />

                <div className="drawer-row drawer-total">
                  <span>{t('Total')}</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
              </div>

              <button 
                className="drawer-checkout"
                onClick={() => {
                  closeCart();
                  navigate('/checkout');
                }}
              >
                {t('Checkout')}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>

              <button className="drawer-continue" onClick={closeCart}>
                {t('Continue Shopping')}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Cart;
